import React, { useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  titleStyles,
  closeIconStyles,
  titleBoxStyles,
  buttonsStyles,
  discardButtonStyles,
  saveButtonStyles,
  boxStyles,
  middleBlockStyles,
  discardButtonEnableStyles
} from './processingMerge.styles';
import { IProcessingMerge } from './processingMerge.models';
import ProcessingMergeBody from './processingMergeBody/processingMergeBody.component';
import { IProcessingMergeJoinBlock } from './processingMergeBody/processingMergeJoin/processingMergeJoinInnerBlock/processingMergeJoinInnerBlock.models';
import { processingMergeJoinEmptyBlock } from './processingMergeBody/processingMergeJoin/processingMergeJoinInnerBlock/processingMergeJoinInnerBlock.constants';
import { emptyBlock } from '../processingPreprocess/processingPreprocess.constants';
import ProcessingSaveModal from '../processingSaveModal/processingSaveModal.component';
import { dispatch, useAppSelector } from '../../../store/hooks';
import { dataProcessingStatusMiddleware } from '../../../store/slices/dataProcessingStatus';
import { storageMiddleware } from '../../../store/slices/storage';
import { toast } from 'react-toastify';
import API from '../../../manager/API';
import { modelVisualisationsMiddleware } from '../../../store/slices/modelVisualisations';
import { summaryMiddleware } from '../../../store/slices/summary';
import { removeLastExtension } from '../../../helpers/removeExtension';
import { dataProcessingMergeMiddleware } from '../../../store/slices/dataProcessingMerge';
import { dataProcessingMergeProgressMiddleware } from '../../../store/slices/dataProcessingMergeProgress';
import { resourcesMiddleware, resourcesSelector } from '../../../store/slices/resources';

const ProcessingMerge: React.FC<IProcessingMerge> = ({
  isDuplicateChecked,
  setIsDuplicateChecked,
  data,
  setData,
  selectedMergeJoinOption,
  setSelectedMergeJoinOption,
  extensionError,
  setExtensionError,
  processingMergeJoinBlocks,
  setProcessingMergeJoinBlocks,
  processingMergeNewFileNameValue,
  setProcessingMergeNewFileNameValue,
  processingMergeUploadStatus,
  setProcessingMergeUploadStatus,
  processingMergeUploadProgress,
  setProcessingMergeUploadProgress,
  processingMergeUploadSucceeded,
  setProcessingMergeUploadSucceeded,
  open,
  onClose,
  summaryData,
  processingMergeInputData,
  setProcessingMergeInputData,
  processingMergeActiveStep,
  setProcessingMergeActiveStep
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [saveProgress, setSaveProgress] = useState<number>(0);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [statusId, setStatusId] = useState<string>('');
  const [isStop, setIsStop] = useState<boolean>(false);
  const [stopped, setStopped] = useState<boolean>(false);
  const stoppedRef = React.useRef(stopped);
  const isDisabled =
    selectedMergeJoinOption && selectedMergeJoinOption?.label && selectedMergeJoinOption?.label !== 'concat'
      ? selectedMergeJoinOption?.label?.length > 0 &&
        processingMergeJoinBlocks?.every(
          (block: IProcessingMergeJoinBlock) => block.singleActionBody?.isCompleted === true
        ) &&
        processingMergeNewFileNameValue.length > 0 &&
        !extensionError
      : selectedMergeJoinOption?.label.length > 0 && processingMergeNewFileNameValue.length > 0 && !extensionError;

  const isDisabledButton =
    processingMergeInputData.dataset.length > 0 ||
    processingMergeInputData.uploadedFileName.length > 0 ||
    processingMergeNewFileNameValue.length > 0 ||
    (selectedMergeJoinOption && selectedMergeJoinOption?.label && selectedMergeJoinOption?.label.length > 0) ||
    processingMergeJoinBlocks?.every(
      (block: IProcessingMergeJoinBlock) => block.singleActionBody?.isCompleted === true
    );

  const getAllResourcesURL = `/datasource/all-files`;

  const steps =
    selectedMergeJoinOption && selectedMergeJoinOption.id === 'concat'
      ? {}
      : {
          initialDataset: processingMergeJoinBlocks.map(
            (block: IProcessingMergeJoinBlock) => block.singleActionBody?.initialDataset?.[0] || ''
          ),
          secondDataset: processingMergeJoinBlocks.map(
            (block: IProcessingMergeJoinBlock) => block.singleActionBody?.secondDataset?.[0] || ''
          )
        };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleDiscard = () => {
    setProcessingMergeJoinBlocks([processingMergeJoinEmptyBlock({ id: 1 })]);
    // setProcessingMergeJoinBlocks([emptyBlock({ id: 1, singleActionBody: {} })]);
    setProcessingMergeActiveStep(1);
    setProcessingMergeUploadProgress(0);
    setProcessingMergeUploadStatus('');
    setProcessingMergeUploadSucceeded(false);
    setProcessingMergeNewFileNameValue('');
    setSelectedMergeJoinOption(null);
    setProcessingMergeInputData({
      dataset: '',
      uploadedFileName: '',
      uploadedFileSize: 0
    });
  };
  const { resourcesData } = useAppSelector(resourcesSelector.resourcesData);
  const getFileId = (fileName: string) => {
    const file = resourcesData.find((item: any) => item.file_name === fileName);
    return file ? file.file_id : null;
  };

  const handleSave = async () => {
    const params = {
      initialDataset: summaryData.filename,
      secondDataset:
        processingMergeInputData.dataset.length > 0
          ? `${processingMergeInputData.dataset}.csv`
          : processingMergeInputData?.uploadedFileName,
      joinMethod: selectedMergeJoinOption.id,
      joinColumns: steps,
      dropDuplicates: isDuplicateChecked,
      newDatasetName: `${processingMergeNewFileNameValue}.csv`
    };
    setModalOpen(true);
    try {
      const dataProcessingMergeUrl = '/data_operations/data_merging';
      const response = await dispatch(
        dataProcessingMergeMiddleware.dataProcessingMerge(params, dataProcessingMergeUrl)
      );
      if (response?.status !== 200) {
        handleCloseModal();
      }
      const saveMergeData = response?.data;
      if (saveMergeData && Object.keys(saveMergeData).length > 0 && saveMergeData?.status_update) {
        setStatusId(saveMergeData?.status_update);
        const saveMergeStatusUrl = `/data_operations/data_merging_status/${saveMergeData.status_update}`;
        const getSaveMergeProgressURL = `/data_operations/data_merging_progress/${saveMergeData.status_update}`;
        let intervalId: any = setInterval(async () => {
          if (stoppedRef.current) {
            clearInterval(intervalId);
            return;
          }
          try {
            const saveMergeStatusData = await dispatch(
              dataProcessingStatusMiddleware.dataProcessingStatus(saveMergeStatusUrl)
            );
            const dataOfSaveMergeProgress = await dispatch(
              dataProcessingMergeProgressMiddleware.getDataProcessingMergeProgress(getSaveMergeProgressURL)
            );
            if (saveMergeStatusData.status === 200) {
              if (
                dataOfSaveMergeProgress &&
                dataOfSaveMergeProgress.data.progress &&
                dataOfSaveMergeProgress.data.progress.length > 0
              ) {
                setSaveProgress(dataOfSaveMergeProgress.data.progress);
                setSaveStatus(dataOfSaveMergeProgress.data.description);
              }

              if (saveMergeStatusData.data.queue_state === 'SUCCESS') {
                const getStorageURL = `blob_file/get_user_storage_size`;
                dispatch(storageMiddleware.getStorage(getStorageURL));
                clearInterval(intervalId);
                handleCloseModal();
                onClose();
                toast.success(saveMergeStatusData.data.status);
                const getPreviewUrl = `/data_operations/file_preview?filename=${
                  processingMergeNewFileNameValue + '.csv'
                }`;
                const getSummaryUrl = `/data_operations/data_summary?file_name=${
                  processingMergeNewFileNameValue + '.csv'
                }`;
                const fetchPreviewData = async () => {
                  try {
                    const response = await API.preview.getPreview(getPreviewUrl);
                    if (!response) {
                      throw new Error('Failed to fetch preview data');
                    }
                    if (response.data && response.data.file_id) {
                      const getVisualisationsUrl = `/visualisations/get_visualisations?id=${response.data.file_id}`;
                      dispatch(modelVisualisationsMiddleware.getModelVisualisations(getVisualisationsUrl));
                    }
                    setData(response.data);
                  } catch (error) {
                    console.error('Error fetching preview data:', error);
                  } finally {
                  }
                };

                fetchPreviewData();
                dispatch(summaryMiddleware.getSummary(getSummaryUrl));
              } else if (
                saveMergeStatusData.data.queue_state === 'FAILURE' ||
                (saveMergeStatusData && Object.keys(saveMergeStatusData).length === 0)
              ) {
                handleCloseModal();
                clearInterval(intervalId);
                if (saveMergeStatusData && Object.keys(saveMergeStatusData).length === 0) {
                  handleCloseModal();
                  toast.error(removeLastExtension(saveMergeStatusData.data.message));
                } else {
                  handleCloseModal();
                  toast.error(removeLastExtension(saveMergeStatusData.data.message));
                }
              }
            } else {
              clearInterval(intervalId);
              toast.error(removeLastExtension(saveMergeStatusData.data.message));
              handleCloseModal();
            }
          } catch (error: any) {
            clearInterval(intervalId);
            toast.error(removeLastExtension(error?.response.data.message));
            handleCloseModal();
          }
        }, 5000);
      }
    } catch (error: any) {
      console.error('Upload failed with error:', error?.message);
      toast.error(removeLastExtension(error?.response?.data?.message));
      handleCloseModal();
    }
  };

  useEffect(() => {
    stoppedRef.current = stopped;
  }, [stopped]);

  // useEffect(() => {
  //   dispatch(resourcesMiddleware.getResources(getAllResourcesURL));
  // }, []);

  return (
    <>
      <Box sx={boxStyles(open)}>
        <Box sx={titleBoxStyles}>
          <span style={titleStyles}>Merge Datasets</span>
          <IconButton onClick={onClose} sx={closeIconStyles}>
            <CloseIcon />
          </IconButton>
        </Box>
        <div style={middleBlockStyles}>
          <ProcessingMergeBody
            isDuplicateChecked={isDuplicateChecked}
            setIsDuplicateChecked={setIsDuplicateChecked}
            onClose={onClose}
            data={data}
            setData={setData}
            selectedMergeJoinOption={selectedMergeJoinOption}
            setSelectedMergeJoinOption={setSelectedMergeJoinOption}
            extensionError={extensionError}
            setExtensionError={setExtensionError}
            processingMergeJoinBlocks={processingMergeJoinBlocks}
            setProcessingMergeJoinBlocks={setProcessingMergeJoinBlocks}
            summaryData={summaryData}
            processingMergeInputData={processingMergeInputData}
            setProcessingMergeInputData={setProcessingMergeInputData}
            processingMergeActiveStep={processingMergeActiveStep}
            setProcessingMergeActiveStep={setProcessingMergeActiveStep}
            processingMergeUploadStatus={processingMergeUploadStatus}
            setProcessingMergeUploadStatus={setProcessingMergeUploadStatus}
            processingMergeUploadProgress={processingMergeUploadProgress}
            setProcessingMergeUploadProgress={setProcessingMergeUploadProgress}
            processingMergeUploadSucceeded={processingMergeUploadSucceeded}
            setProcessingMergeUploadSucceeded={setProcessingMergeUploadSucceeded}
            processingMergeNewFileNameValue={processingMergeNewFileNameValue}
            setProcessingMergeNewFileNameValue={setProcessingMergeNewFileNameValue}
          />
        </div>
        <div style={buttonsStyles}>
          <button style={!isDisabledButton ? discardButtonStyles : discardButtonEnableStyles} onClick={handleDiscard}>
            Discard
          </button>
          <button onClick={handleSave} style={saveButtonStyles(isDisabled)} disabled={!isDisabled}>
            Save
          </button>
        </div>
      </Box>
      <ProcessingSaveModal
        open={modalOpen}
        onClose={handleCloseModal}
        uploadStatus={saveStatus}
        setUploadStatus={setSaveStatus}
        setStopped={setStopped}
        isStop={isStop}
        setIsStop={setIsStop}
        statusId={statusId}
        progress={saveProgress}
        setProgress={setSaveProgress}
        onContinue={handleCloseModal}
        fileName={processingMergeNewFileNameValue}
        isMerge
      />
    </>
  );
};

export default ProcessingMerge;
